import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'notify';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  onClick?: () => void;
}

export interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  notify: (message: string, opts?: { onClick?: () => void }) => void;
}

interface ToastContextValue extends ToastApi {}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_TTL_MS = 4500;
const NOTIFY_TTL_MS = 12000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((items) => items.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    (type: ToastType, message: string, onClick?: () => void) => {
      const text = message.trim();
      if (!text) return;
      const id = Date.now() + Math.floor(Math.random() * 1000);
      setToasts((items) => [...items, { id, type, message: text, onClick }]);
      window.setTimeout(() => dismiss(id), type === 'notify' ? NOTIFY_TTL_MS : TOAST_TTL_MS);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message: string) => push('success', message),
      error: (message: string) => push('error', message),
      info: (message: string) => push('info', message),
      notify: (message: string, opts?: { onClick?: () => void }) =>
        push('notify', message, opts?.onClick),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}${toast.onClick ? ' toast-clickable' : ''}`}
            role="status"
            onClick={() => {
              const action = toast.onClick;
              dismiss(toast.id);
              action?.();
            }}
          >
            {toast.message.split('\n').map((line, i) => (
              <div key={i} className={i === 0 ? 'toast-title' : 'toast-body'}>
                {line}
              </div>
            ))}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
